import React, { createContext, useContext, useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Course } from "../types/Course";
import {
  filterData,
  initialState,
  majorOptions,
  stateOptions,
} from "../Data/courseFilterData";

interface CourseContextType {
  courses: Course[];
  setCourses: React.Dispatch<React.SetStateAction<Course[]>>;
  metadata: any | null;
  loading: boolean;
  metadataLoading: boolean;
  error: string | null;
  unfilteredCourses: any[];
  setUnfilteredCourses: (courses: any[]) => void;
  unfilteredTotalCount: number | null;
  setUnfilteredTotalCount: (count: number | null) => void;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

const shouldFetchFirestoreMetadata =
  import.meta.env.VITE_ENABLE_FIRESTORE_COURSE_METADATA === "true";

const toMetadataOptions = (
  options: Array<{ value: string; label: string }>,
  labelKey: string,
) =>
  options
    .filter((option) => option.value)
    .map((option) => ({
      slug: option.value,
      value: option.value,
      label: option.label,
      [labelKey]: option.label,
    }));

const defaultMetadata = {
  fieldOfStudy: toMetadataOptions(
    filterData.courseInfo.fieldOfStudy.options,
    "fieldOfStudy",
  ),
  level: toMetadataOptions(filterData.courseInfo.level.options, "level"),
  intake: [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEPT",
    "OCT",
    "NOV",
    "DEC",
  ],
  overseasTransfer: toMetadataOptions(
    filterData.location.overseasTransfer.options,
    "overseasTransfer",
  ),
  partTimeOption: ["Yes", "No"],
  country: toMetadataOptions(filterData.location.country.options, "country"),
  abroadDuration: toMetadataOptions(
    filterData.financials.durationAbroad.options,
    "abroadDuration",
  ),
  tuitionFee: initialState.financials.feeBudget,
  duration: initialState.financials.fullTimeDuration,
  universities: [],
  ...Object.fromEntries(
    Object.entries(majorOptions).map(([fieldSlug, options]) => {
      const fieldLabel =
        filterData.courseInfo.fieldOfStudy.options.find(
          (option) => option.value === fieldSlug,
        )?.label || fieldSlug;

      return [fieldLabel, toMetadataOptions(options, "major")];
    }),
  ),
  ...Object.fromEntries(
    Object.entries(stateOptions).map(([countrySlug, options]) => {
      const countryLabel =
        filterData.location.country.options.find(
          (option) => option.value === countrySlug,
        )?.label || countrySlug;

      return [countryLabel, toMetadataOptions(options, "state")];
    }),
  ),
};

export const CourseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [metadata, setMetadata] = useState<any | null>(defaultMetadata);
  const [loading, setLoading] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(
    shouldFetchFirestoreMetadata,
  );
  const [error, setError] = useState<string | null>(null);

  const [unfilteredCourses, setUnfilteredCourses] = useState<any[]>([]);
  const [unfilteredTotalCount, setUnfilteredTotalCount] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!shouldFetchFirestoreMetadata) return;

    const fetchMetadata = async () => {
      setMetadataLoading(true);

      try {
        const metadataRef = doc(db, "metadata", "course-metadata");
        const docSnap = await getDoc(metadataRef);

        if (docSnap.exists()) {
          setMetadata({ ...defaultMetadata, ...docSnap.data() });
        }
      } catch (err) {
        console.warn(
          "Course metadata is unavailable; using bundled filter options.",
          err,
        );
      } finally {
        setMetadataLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  return (
    <CourseContext.Provider
      value={{
        courses,
        setCourses,
        metadata,
        loading,
        metadataLoading,
        error,
        unfilteredCourses,
        setUnfilteredCourses,
        unfilteredTotalCount,
        setUnfilteredTotalCount,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
};

export const useCourses = () => {
  const context = useContext(CourseContext);
  if (!context)
    throw new Error("useCourses must be used inside CourseProvider");
  return context;
};
